import React from 'react';
import s from './s.module.less';
import { Spin, Image, Tag, Modal } from 'antd';
import dayjs from 'dayjs';
import { TextVisitCase } from 'types/textVisit';
import { capitalize } from 'lodash';
import { ExpandOutlined } from '@ant-design/icons';
import { IVideoTreatmentDetail } from 'types/treatment';
import { parseSpecificIntakeFormGroups } from 'utils/intakeAssessmentScores';
import SpecificIntakeFormAssessmentBlock from './components/SpecificIntakeFormAssessmentBlock';

type Props = {
    textVisitDetail?: TextVisitCase; //二选一来渲染，然后兼容数据结构，太恶心了
    appointmentTreatmentDetail?: IVideoTreatmentDetail;
};
//https://www.figma.com/design/wVYrOpKFq1r8nGfiHu3eou/Patient-Portal?node-id=7887-26216&m=dev
//not same ui with antd

const IntakeFormContent = (props: Props) => {
    const { textVisitDetail, appointmentTreatmentDetail } = props;
    const data = textVisitDetail || appointmentTreatmentDetail;
    const speciality = data?.conditions;
    const serviceTypeName = data?.serviceType;
    const [imageModalVisible, setImageModalVisible] = React.useState(false);
    const [currentImage, setCurrentImage] = React.useState<string>('');

    const { navItems, groupedIntakeAnswers, commonIntakeAnswers } = React.useMemo(
        () =>
            parseSpecificIntakeFormGroups(
                textVisitDetail?.formGroups || appointmentTreatmentDetail?.formGroups,
            ),
        [textVisitDetail, appointmentTreatmentDetail],
    );
    const showIntakeFormCommon = commonIntakeAnswers.length > 0;
    const hasStructuredIntakeQuestions = navItems.length > 0 || showIntakeFormCommon;

    const renderPharmacy = (): React.ReactNode => {
        if (data && data.pharmacy) {
            return (
                <>
                    <div>{data.pharmacy.name}</div>
                    <div>{data.pharmacy.address}</div>
                    <div>
                        {data.pharmacy.city}, {data.pharmacy.state} {data.pharmacy.zipCode}
                    </div>
                </>
            );
        }
        return '-';
    };

    const renderHomeAddress = (): React.ReactNode => {
        if (data?.prospect?.address) {
            return (
                <>
                    <div>{data.prospect.address}</div>
                    {data.prospect.city && data.prospect.state && (
                        <div>
                            {data.prospect.city}, {data.prospect.state} {data.prospect.zip || ''}
                        </div>
                    )}
                </>
            );
        }
        return '-';
    };

    const renderAnswerItem = (ans: unknown, idx: number, showBullet = true, shouldMask = false) => {
        if (typeof ans === 'string') {
            const displayText = ans;
            if (!showBullet) {
                return displayText;
            }
            return (
                <div key={idx} className={s.motivationItem}>
                    • {displayText}
                </div>
            );
        }

        if (typeof ans === 'object' && ans !== null) {
            const answerObj = ans as {
                option?: string;
                images?: string[];
                imageContentUri?: string;
            };

            if (shouldMask) {
                if (answerObj?.option) {
                    const maskedOption = answerObj.option;
                    return (
                        <div key={idx} className={s.motivationItem}>
                            {showBullet && '• '}
                            {maskedOption}
                        </div>
                    );
                }
                return (
                    <div key={idx} className={s.motivationItem}>
                        {showBullet && '• '}
                        *****
                    </div>
                );
            }

            let images: string[] = [];
            if (answerObj?.images && Array.isArray(answerObj.images)) {
                images = answerObj.images.map((resourceKey) => resourceKey).filter((uri) => !!uri);
            } else if (answerObj?.imageContentUri) {
                images = [answerObj.imageContentUri];
            }

            if (answerObj?.option && images.length > 0) {
                const shouldShowOption = answerObj.option !== 'CUSTOM';
                return (
                    <div key={idx} className={s.motivationItem}>
                        {showBullet && '• '}
                        <div className={s.motivationImageContainer}>
                            <Image.PreviewGroup
                                // @ts-ignore
                                items={images}
                            >
                                {images
                                    .filter((uri) => !!uri)
                                    .map((uri) => (
                                        <div key={uri} className={s.motivationImageWrapper}>
                                            <div style={{ position: 'relative' }}>
                                                <Image
                                                    className={s.motivationImage}
                                                    src={uri}
                                                    preview={{
                                                        src: uri,
                                                        mask: false,
                                                    }}
                                                />
                                                <div
                                                    className={s.expandIcon}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const imageElement =
                                                            e.currentTarget.parentElement?.querySelector('img');
                                                        if (imageElement) {
                                                            imageElement.click();
                                                        }
                                                    }}
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="14"
                                                        height="14"
                                                        viewBox="0 0 14 14"
                                                        fill="none"
                                                    >
                                                        <path
                                                            d="M2.33203 4.66665V2.33331M2.33203 2.33331H4.66536M2.33203 2.33331L5.2487 5.24998M11.6654 4.66665V2.33331M11.6654 2.33331H9.33203M11.6654 2.33331L8.7487 5.24998M2.33203 9.33331V11.6666M2.33203 11.6666H4.66536M2.33203 11.6666L5.2487 8.74998M11.6654 11.6666L8.7487 8.74998M11.6654 11.6666V9.33331M11.6654 11.6666H9.33203"
                                                            stroke="white"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                </div>
                                            </div>
                                            {shouldShowOption && (
                                                <div className={s.motivationImageOption}>{answerObj.option}</div>
                                            )}
                                        </div>
                                    ))}
                            </Image.PreviewGroup>
                        </div>
                    </div>
                );
            }
        }

        const displayValue = String(ans);
        if (!showBullet) {
            return displayValue;
        }
        return (
            <div key={idx} className={s.motivationItem}>
                • {displayValue}
            </div>
        );
    };

    const renderAnswerContent = (
        answer: unknown,
        showBullet = true,
        shouldMask = false,
        sensitiveType: 'name' | 'date' | 'phone' | 'address' | 'pharmacy' | null = null,
    ): React.ReactNode => {
        if (!answer) return '-';

        let parsedAnswer = answer;
        if (typeof answer === 'string') {
            try {
                parsedAnswer = JSON.parse(answer);
            } catch {
                return answer;
            }
        }

        if (!Array.isArray(parsedAnswer)) {
            parsedAnswer = [parsedAnswer];
        }

        const filteredAnswer = (parsedAnswer as unknown[]).filter((a: unknown) => a != null);

        if (filteredAnswer.length === 0) return '-';

        const hasImages = filteredAnswer.some((item) => {
            if (typeof item === 'object' && item !== null) {
                const obj = item as { images?: string[] };
                return Array.isArray(obj.images) && obj.images.length > 0;
            }
            return false;
        });

        if (filteredAnswer.length === 1 && typeof filteredAnswer[0] === 'string' && !hasImages) {
            return filteredAnswer[0];
        }

        return (
            <>{filteredAnswer.map((ans: unknown, idx: number) => renderAnswerItem(ans, idx, showBullet, shouldMask))}</>
        );
    };

    let commonSection: React.ReactNode = null;
    if (showIntakeFormCommon) {
        commonSection = (
            <div className={s.intakeCommonContent}>
                {commonIntakeAnswers.map((item) => (
                    <div key={item.id} className={s.intakeCommonItem}>
                        <div className={s.intakeCommonItemQuestion}>{item.fullQuestionName}</div>
                        <div className={s.intakeCommonItemAnswer}>{item.answer || '-'}</div>
                    </div>
                ))}
            </div>
        );
    } else if (!hasStructuredIntakeQuestions && data?.intakeQuestions?.length) {
        commonSection = (
            <div className={s.intakeCommonContent}>
                {data.intakeQuestions
                    .filter((q) => q?.answer)
                    .map((q) => (
                        <div key={q.id} className={s.intakeCommonItem}>
                            <div className={s.intakeCommonItemQuestion}>
                                {q.shortQuestionName || q.fullQuestionName}
                            </div>
                            <div className={s.intakeCommonItemAnswer}>{renderAnswerContent(q.answer, true)}</div>
                        </div>
                    ))}
            </div>
        );
    }

    const getIntakeAnswerWithImages = (questionName: string): React.ReactNode => {
        const question = data?.intakeQuestions?.find((q) =>
            q.fullQuestionName?.toLowerCase().includes(questionName.toLowerCase()),
        );
        if (!question || !question.answer) return '-';
        return renderAnswerContent(question.answer);
    };

    const handleImageClick = (imageUrl: string) => {
        setCurrentImage(imageUrl);
        setImageModalVisible(true);
    };

    if (!data) {
        return <Spin />;
    }

    return (
        <div className={s.wrap}>
            <div className={s.title}>
                <span>
                    [{data.patientFirstName} {data.patientLastName}] {speciality}: {serviceTypeName}
                </span>
                <span className={s.time}>Submitted on {dayjs(data.assignedProviderAt).format('MMM D, YYYY')}</span>
            </div>
            <div className={s.header}>
                <div className={s.hItem}>
                    <div className={s.hItemLabel}>Legal name</div>
                    <div className={s.hItemValue}>
                        {data.patientFirstName} {data.patientLastName}
                    </div>
                </div>
                <div className={s.hItem}>
                    <div className={s.hItemLabel}>Date of birth</div>
                    <div className={s.hItemValue}>
                        {`${dayjs(data.patientBirthday, 'YYYY-MM-DD').format('MM/DD/YYYY')} (${data.patientAge} year-old)`}
                    </div>
                </div>
                <div className={s.hItem}>
                    <div className={s.hItemLabel}>Gender</div>
                    <div className={s.hItemValue}>{capitalize(data.patientGender || '-')}</div>
                </div>
                <div className={s.hItem}>
                    <div className={s.hItemLabel}>Martial status</div>
                    <div className={s.hItemValue}>{capitalize(data?.maritalStatus || '-')}</div>
                </div>
                <div className={s.hItem}>
                    <div className={s.hItemLabel}>Phone number</div>
                    <div className={s.hItemValue}>{data.prospect?.tel || '-'}</div>
                </div>
                <div className={s.hItem}>
                    <div className={s.hItemLabel}>Pharmacy</div>
                    <div className={s.hItemValue}>{renderPharmacy()}</div>
                </div>
                <div className={s.hItem}>
                    <div className={s.hItemLabel}>Home address</div>
                    <div className={s.hItemValue}>{renderHomeAddress()}</div>
                </div>
                <div className={s.hItem}>
                    <div className={s.hItemLabel}>Emergency Contact</div>
                    <div className={s.hItemValue}>
                        {data.emergencyContacts.map((contact) => (
                            <div key={contact.id}>
                                <p className={s.emergencyContactName}>{contact.fullName}</p>
                                <p className={s.emergencyContactPhone}>{contact.phone}</p>
                            </div>
                        ))}
                    </div>
                </div>
                <div className={s.hItem}>
                    <div className={s.hItemLabel}>Visit goal</div>
                    <div className={s.hItemValue}>{getIntakeAnswerWithImages('visit goal') || '-'}</div>
                </div>
            </div>
            <div className={s.content}>
                <div className={s.cItem}>
                    <div className={s.cLabel}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M10.2851 1.27099C10.1749 1.28517 10.0497 1.32366 9.94723 1.37492C9.67402 1.51155 9.51754 1.69931 9.22966 2.23592C9.14281 2.39781 9.11137 2.45217 9.05607 2.53609C8.91419 2.75139 8.72946 2.92667 8.50176 3.06205C8.46156 3.08595 8.34812 3.14913 8.24967 3.20243C8.04446 3.31355 7.98937 3.34572 7.87863 3.4191C7.73408 3.51488 7.63295 3.59941 7.56382 3.68222C7.45939 3.80734 7.39383 3.92959 7.34764 4.08531C7.31763 4.18652 7.30884 4.25357 7.30884 4.38129C7.30884 4.50901 7.31763 4.57606 7.34764 4.67727C7.38886 4.81626 7.4385 4.91559 7.52511 5.0324C7.64571 5.19504 7.84708 5.34295 8.2254 5.54676C8.55134 5.72234 8.63361 5.77552 8.77801 5.90391C8.84254 5.96128 8.93427 6.05989 8.9875 6.1291C9.0606 6.22416 9.11943 6.32143 9.24625 6.55689C9.34883 6.74736 9.37993 6.80094 9.44915 6.90652C9.61773 7.16365 9.74512 7.2868 9.94626 7.38708C10.0253 7.4265 10.1227 7.46045 10.2086 7.4786C10.3014 7.49819 10.4739 7.50344 10.5723 7.48967C10.7857 7.45981 10.9878 7.36573 11.1448 7.22321C11.2747 7.10522 11.4089 6.91457 11.5646 6.62682C11.785 6.21958 11.8408 6.13511 11.9926 5.97849C12.1411 5.82536 12.2561 5.7455 12.5818 5.56952C12.8046 5.44912 12.8457 5.42522 12.9591 5.35024C13.1269 5.23921 13.2325 5.14862 13.3117 5.04769C13.5553 4.73703 13.6113 4.32066 13.4576 3.96209C13.3764 3.7728 13.2657 3.63502 13.0879 3.502C12.9518 3.40023 12.8477 3.33677 12.5774 3.19073C12.4815 3.1389 12.3731 3.07848 12.3366 3.05647C12.0805 2.90205 11.8784 2.69467 11.7258 2.42969C11.7072 2.39735 11.6573 2.30663 11.615 2.22809C11.5122 2.03731 11.4753 1.97284 11.4164 1.88044C11.3602 1.79252 11.2706 1.67063 11.2252 1.62049C11.0528 1.43032 10.8244 1.30814 10.5766 1.27357C10.5096 1.26423 10.3487 1.26281 10.2851 1.27099ZM10.4001 2.27022C10.3757 2.27874 10.3745 2.28003 10.3189 2.35689C10.269 2.42588 10.2283 2.49495 10.1321 2.67329C9.90048 3.10298 9.7745 3.28265 9.54316 3.51329C9.3274 3.72838 9.1388 3.86094 8.75066 4.07029C8.51473 4.19754 8.46298 4.22841 8.36944 4.29776C8.31906 4.33511 8.30826 4.34985 8.30826 4.38129C8.30826 4.39178 8.31122 4.40685 8.31483 4.41479C8.32878 4.44539 8.47575 4.54324 8.64706 4.63597C8.87516 4.75945 8.94126 4.79625 9.02218 4.84485C9.37283 5.05543 9.6633 5.33217 9.88947 5.67113C9.95615 5.77107 10.019 5.87939 10.1334 6.09166C10.227 6.26552 10.2766 6.34846 10.3327 6.42545C10.382 6.49322 10.4021 6.506 10.4421 6.49523C10.4903 6.48225 10.5678 6.36668 10.7275 6.06969C10.7747 5.98191 10.8346 5.87355 10.8606 5.82889C11.0836 5.44614 11.3656 5.14598 11.7246 4.90919C11.8268 4.84182 11.8972 4.80108 12.1197 4.68061C12.3103 4.57737 12.3891 4.53085 12.4607 4.4791C12.5294 4.42945 12.5391 4.41735 12.5391 4.38129C12.5391 4.34522 12.5294 4.33312 12.4607 4.28347C12.389 4.23166 12.3102 4.18515 12.1191 4.08167C12.0205 4.02832 11.9033 3.96328 11.8587 3.93713C11.5386 3.74978 11.2711 3.51389 11.0572 3.23049C10.9474 3.08494 10.8732 2.96391 10.7275 2.69289C10.6131 2.48023 10.5575 2.38811 10.4985 2.31358C10.4621 2.26757 10.438 2.25694 10.4001 2.27022ZM0.626976 2.76835C0.435742 2.80315 0.282654 2.94451 0.229561 3.1353C0.215146 3.18709 0.215146 3.33549 0.229561 3.38728C0.273398 3.5448 0.395265 3.67835 0.54465 3.73258C0.632946 3.76463 0.45262 3.76271 3.23746 3.76114L5.79666 3.75969L5.84146 3.747C5.93766 3.71975 6.01098 3.67477 6.08166 3.59965C6.13429 3.5437 6.16906 3.486 6.19275 3.41529C6.21015 3.36335 6.21057 3.3597 6.21057 3.26129C6.21057 3.16287 6.21015 3.15923 6.19275 3.10729C6.16906 3.03658 6.13429 2.97888 6.08166 2.92293C6.01098 2.84781 5.93766 2.80283 5.84146 2.77558L5.79666 2.76289L3.23186 2.76197C1.14578 2.76123 0.659579 2.76242 0.626976 2.76835ZM0.626976 6.01635C0.435742 6.05115 0.282654 6.19251 0.229561 6.3833C0.215146 6.43509 0.215146 6.58349 0.229561 6.63528C0.273398 6.7928 0.395265 6.92635 0.54465 6.98058C0.632946 7.01263 0.45262 7.01071 3.23746 7.00914L5.79666 7.00769L5.84146 6.995C5.93766 6.96775 6.01098 6.92277 6.08166 6.84765C6.13429 6.7917 6.16906 6.734 6.19275 6.66329C6.21015 6.61135 6.21057 6.6077 6.21057 6.50929C6.21057 6.41087 6.21015 6.40723 6.19275 6.35529C6.16906 6.28458 6.13429 6.22688 6.08166 6.17093C6.01098 6.09581 5.93766 6.05083 5.84146 6.02358L5.79666 6.01089L3.23186 6.00997C1.14578 6.00923 0.659579 6.01042 0.626976 6.01635ZM0.695061 9.26241C0.536413 9.28224 0.388735 9.38838 0.3143 9.53609C0.257326 9.64914 0.247856 9.79479 0.289408 9.91882C0.309798 9.97967 0.32989 10.0161 0.372915 10.0703C0.438217 10.1524 0.519658 10.2077 0.623342 10.2402L0.672661 10.2557H6.90266H13.1327L13.1831 10.24C13.347 10.1888 13.4781 10.0561 13.5242 9.89449C13.5465 9.81627 13.5455 9.69984 13.5219 9.61539C13.5008 9.54021 13.456 9.46626 13.3949 9.40588C13.3079 9.31989 13.2184 9.27585 13.102 9.26181C13.0422 9.25459 0.752853 9.25518 0.695061 9.26241ZM0.695061 12.5104C0.536413 12.5302 0.388735 12.6364 0.3143 12.7841C0.257326 12.8971 0.247856 13.0428 0.289408 13.1668C0.309798 13.2277 0.32989 13.2641 0.372915 13.3183C0.438217 13.4004 0.519658 13.4557 0.623342 13.4882L0.672661 13.5037H6.90266H13.1327L13.1831 13.488C13.347 13.4368 13.4781 13.3041 13.5242 13.1425C13.5465 13.0643 13.5455 12.9478 13.5219 12.8634C13.5008 12.7882 13.456 12.7143 13.3949 12.6539C13.3079 12.5679 13.2184 12.5238 13.102 12.5098C13.0422 12.5026 0.752853 12.5032 0.695061 12.5104Z"
                                fill="url(#paint0_linear_8318_26341)"
                            />
                            <defs>
                                <linearGradient
                                    id="paint0_linear_8318_26341"
                                    x1="3.50342"
                                    y1="6.99902"
                                    x2="14.0034"
                                    y2="13.999"
                                    gradientUnits="userSpaceOnUse"
                                >
                                    <stop stopColor="#E45EFF" />
                                    <stop offset="1" stopColor="#3C3FFF" />
                                </linearGradient>
                            </defs>
                        </svg>
                        AI summary
                    </div>
                    <div className={s.cVal}>
                        <div>{data.aiSummary}</div>
                        <div>
                            <div className={s.tags} style={{ marginTop: '6px' }}>
                                {data.aiTags &&
                                    data.aiTags.split(',').map((tag, index) => (
                                        <div key={index} className={s.summaryTag}>
                                            {tag}
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </div>
                </div>
                <div className={s.cItem}>
                    <div className={s.cLabel}>Medical history</div>
                    <div className={s.cVal}>
                        <div className={s.tags}>
                            {data.historyMedical?.length ? (
                                data.historyMedical?.map((item, index) => (
                                    <Tag key={index} className={s.tag}>
                                        {item}
                                    </Tag>
                                ))
                            ) : (
                                <div className="text-gray-900">No medical history provided</div>
                            )}
                        </div>
                    </div>
                </div>
                <div className={s.cItem}>
                    <div className={s.cLabel}>Current medications</div>
                    <div className={s.cVal}>
                        <div className={s.tags}>
                            {data.currentMedications?.length ? (
                                data.currentMedications?.map((item, index) => (
                                    <Tag key={index} className={s.tag}>
                                        {item}
                                    </Tag>
                                ))
                            ) : (
                                <div className="text-gray-900">No medications listed</div>
                            )}
                        </div>
                    </div>
                </div>
                <div className={s.cItem}>
                    <div className={s.cLabel}>Allergies</div>
                    <div className={s.cVal}>
                        <div className={s.tags}>
                            {data.allergies?.length ? (
                                data.allergies?.map((item, index) => (
                                    <Tag key={index} className={s.tag}>
                                        {item}
                                    </Tag>
                                ))
                            ) : (
                                <div className="text-gray-900">No allergies reported</div>
                            )}
                        </div>
                    </div>
                </div>
                <div className={s.cItem}>
                    <div className={s.cLabel}>Intake questions</div>
                    <div className={s.cVal}>
                        <div className={s.intakeCotent}>
                            <SpecificIntakeFormAssessmentBlock
                                layout="drawer"
                                navItems={navItems}
                                groupedIntakeAnswers={groupedIntakeAnswers}
                                commonSection={commonSection}
                            />
                        </div>
                    </div>
                </div>
                <div className={s.cItem}>
                    <div className={s.cLabel}>ID</div>
                    <div className={s.cVal}>
                        <div className={s.idLabel}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18ZM13.7071 8.70711C14.0976 8.31658 14.0976 7.68342 13.7071 7.29289C13.3166 6.90237 12.6834 6.90237 12.2929 7.29289L9 10.5858L7.70711 9.29289C7.31658 8.90237 6.68342 8.90237 6.29289 9.29289C5.90237 9.68342 5.90237 10.3166 6.29289 10.7071L8.29289 12.7071C8.68342 13.0976 9.31658 13.0976 9.70711 12.7071L13.7071 8.70711Z"
                                    fill="#34D399"
                                />
                            </svg>
                            ID verified
                        </div>
                        <div className={s.idVal}>
                            {data.patientIdFrontUrl && (
                                <div className={s.idImageContainer}>
                                    <img src={data.patientIdFrontUrl} alt="Front of ID" className={s.idImage} />
                                    <div
                                        className={s.expandIcon}
                                        onClick={() => handleImageClick(data.patientIdFrontUrl!)}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="17"
                                            height="17"
                                            viewBox="0 0 17 17"
                                            fill="none"
                                        >
                                            <path
                                                d="M2.80469 5.61295V2.80664M2.80469 2.80664H5.61099M2.80469 2.80664L6.31257 6.31452M14.0299 5.61295V2.80664M14.0299 2.80664H11.2236M14.0299 2.80664L10.522 6.31452M2.80469 11.2256V14.0319M2.80469 14.0319H5.61099M2.80469 14.0319L6.31257 10.524M14.0299 14.0319L10.522 10.524M14.0299 14.0319V11.2256M14.0299 14.0319H11.2236"
                                                stroke="#0E62F4"
                                                strokeWidth="1.40315"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </div>
                                </div>
                            )}
                            {data.patientIdBackUrl && (
                                <div className={s.idImageContainer}>
                                    <img src={data.patientIdBackUrl} alt="Back of ID" className={s.idImage} />
                                    <div
                                        className={s.expandIcon}
                                        onClick={() => handleImageClick(data.patientIdBackUrl!)}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="17"
                                            height="17"
                                            viewBox="0 0 17 17"
                                            fill="none"
                                        >
                                            <path
                                                d="M2.80469 5.61295V2.80664M2.80469 2.80664H5.61099M2.80469 2.80664L6.31257 6.31452M14.0299 5.61295V2.80664M14.0299 2.80664H11.2236M14.0299 2.80664L10.522 6.31452M2.80469 11.2256V14.0319M2.80469 14.0319H5.61099M2.80469 14.0319L6.31257 10.524M14.0299 14.0319L10.522 10.524M14.0299 14.0319V11.2256M14.0299 14.0319H11.2236"
                                                stroke="#0E62F4"
                                                strokeWidth="1.40315"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <Modal
                open={imageModalVisible}
                onCancel={() => setImageModalVisible(false)}
                footer={null}
                width="80%"
                centered
            >
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <img
                        src={currentImage}
                        alt="ID Document"
                        style={{ width: 'auto', height: 'auto', maxWidth: '80vw', maxHeight: '80vh' }}
                    />
                </div>
            </Modal>
        </div>
    );
};

export default IntakeFormContent;
